import { ArrowRight, CheckCircle2, Loader2, Mail, MessageCircleMore, UserRound } from "lucide-react";
import { FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Brand } from "../components/ui/Brand";
import { setSessionToken } from "../lib/session";
import { useAccount } from "../context/AccountContext";

type ActivationData = {
  account: { displayName: string; email: string; whatsapp: string; planLabel: string; site: { businessName: string; slug: string } | null };
};

export function ActivatePage() {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAccount();
  const [data, setData] = useState<ActivationData | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`/api/activate/${token}`).then(async (response) => {
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || "ACTIVATION_LINK_INVALID");
      setData(body);
      setDisplayName(body.account.displayName || body.account.site?.businessName || "");
      setEmail(body.account.email || "");
    }).catch(() => setError("Tautan aktivasi tidak valid atau sudah kedaluwarsa.")).finally(() => setLoading(false));
  }, [token]);

  const activate = async (event: FormEvent) => {
    event.preventDefault();
    setProcessing(true);
    setError("");
    try {
      const response = await fetch(`/api/activate/${token}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ displayName, email }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || "ACTIVATION_FAILED");
      setSessionToken(body.sessionToken);
      await refresh();
      navigate("/dashboard", { replace: true });
    } catch {
      setError("Aktivasi dashboard belum berhasil. Silakan minta tautan baru melalui WhatsApp.");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <main className="min-h-screen bg-[linear-gradient(135deg,#f0fdf4,#ffffff_52%,#fff7ed)] px-5 py-10">
      <div className="mx-auto max-w-4xl">
        <div className="flex justify-center"><Brand /></div>
        <div className="mt-9 overflow-hidden rounded-[2.25rem] border border-white bg-white/90 shadow-soft backdrop-blur-xl">
          {loading ? <div className="grid min-h-[520px] place-items-center"><Loader2 className="animate-spin text-brand-600" size={34} /></div> : error && !data ? <div className="grid min-h-[520px] place-items-center p-8 text-center"><div><div className="text-6xl">🔐</div><h1 className="mt-4 text-3xl font-black text-slate-900">Aktivasi tidak tersedia</h1><p className="mt-3 text-slate-500">{error}</p></div></div> : data && (
            <div className="grid lg:grid-cols-[0.9fr_1.1fr]">
              <section className="bg-gradient-to-br from-brand-700 to-brand-950 p-8 text-white sm:p-10">
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-xs font-black uppercase tracking-[0.16em] text-brand-100"><CheckCircle2 size={14} /> Upgrade berhasil</span>
                <h1 className="mt-6 text-4xl font-black tracking-[-0.045em]">Aktifkan dashboard {data.account.planLabel}.</h1>
                <p className="mt-4 text-sm leading-7 text-white/65">Website <strong className="text-white">{data.account.site?.businessName}</strong> sudah terhubung. Tidak ada proses import atau pembuatan ulang.</p>
                <div className="mt-8 rounded-3xl border border-white/10 bg-white/[0.07] p-5">
                  <p className="text-xs font-bold uppercase tracking-wider text-white/50">Akun WhatsApp terverifikasi</p>
                  <p className="mt-2 text-xl font-black">{data.account.whatsapp}</p>
                  <p className="mt-5 text-xs font-bold uppercase tracking-wider text-white/50">Website yang sama</p>
                  <p className="mt-2 font-extrabold">csjw.pages.dev/{data.account.site?.slug}</p>
                </div>
              </section>

              <section className="p-8 sm:p-10">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-accent-100 text-accent-700"><MessageCircleMore size={23} /></div>
                <h2 className="mt-5 text-3xl font-black tracking-[-0.04em] text-slate-900">Lengkapi profil dashboard</h2>
                <p className="mt-2 text-sm leading-6 text-slate-500">Cukup satu kali. Login berikutnya memakai nomor WhatsApp dan OTP.</p>
                <form className="mt-7 space-y-5" onSubmit={activate}>
                  <label className="block"><span className="mb-2 block text-sm font-extrabold text-slate-700">Nama pengguna</span><span className="relative block"><UserRound className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} /><input className="field !pl-12" value={displayName} onChange={(event) => setDisplayName(event.target.value)} required /></span></label>
                  <label className="block"><span className="mb-2 block text-sm font-extrabold text-slate-700">Email pemulihan <span className="font-semibold text-slate-400">(opsional)</span></span><span className="relative block"><Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} /><input className="field !pl-12" type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="nama@email.com" /></span></label>
                  {error && <div className="rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm font-bold text-rose-700">{error}</div>}
                  <button type="submit" className="btn-primary w-full !py-3.5" disabled={processing}>{processing ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />}{processing ? "Mengaktifkan..." : "Aktifkan dan buka dashboard"}</button>
                </form>
              </section>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
